import React, { useEffect, useState } from "react";
import { PortalHeader, PortalBody, StatTile, EmptyState } from "@/components/PortalUI";
import api, { formatApiError } from "@/lib/api";
import { Shield, ShieldOff, ToggleLeft, ToggleRight, Save, Play, AlertTriangle, Sparkles, Check, X, Edit3 } from "lucide-react";

export function AdminAgentStudio() {
  const [tab, setTab] = useState("metrics");
  return (
    <>
      <PortalHeader eyebrow="/ ADMIN · SIX" title="Agent Studio" testId="agent-studio-header">
        <div className="flex gap-1 flex-wrap">
          {["metrics","toggles","knowledge","proposals","handoffs","injections","funnel"].map((t) => (
            <button key={t} className={`dt6-btn text-xs ${tab===t?"dt6-btn-primary":""}`} onClick={() => setTab(t)} data-testid={`studio-tab-${t}`}>{t}</button>
          ))}
        </div>
      </PortalHeader>
      <PortalBody>
        {tab === "metrics" && <MetricsTab />}
        {tab === "toggles" && <TogglesTab />}
        {tab === "knowledge" && <KnowledgeTab />}
        {tab === "proposals" && <ProposalsTab />}
        {tab === "handoffs" && <HandoffsTab />}
        {tab === "injections" && <InjectionsTab />}
        {tab === "funnel" && <FunnelTab />}
      </PortalBody>
    </>
  );
}

function MetricsTab() {
  const [m, setM] = useState(null);
  const [brief, setBrief] = useState(null);
  const [tickResult, setTickResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/agent/metrics").then((r) => setM(r.data));
  useEffect(() => { load(); }, []);

  const runBrief = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/agent/daily-brief-now");
      setBrief(data);
    } catch (e) { setBrief({ error: formatApiError(e) }); }
    setBusy(false);
  };

  const tickDelivery = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/agent/tick-auto-delivery");
      setTickResult(data);
      await load();
    } catch (e) { setTickResult({ error: formatApiError(e) }); }
    setBusy(false);
  };

  if (!m) return <EmptyState testId="studio-metrics-loading">LOADING...</EmptyState>;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatTile label="Conversations · 24h" value={m.conversations_24h} testId="studio-conv-24h" />
        <StatTile label="Qualified · 24h" value={m.qualified_24h} testId="studio-qual-24h" />
        <StatTile label="Checkout links · 24h" value={m.checkout_links_issued_24h} testId="studio-checkout-24h" />
        <StatTile label="Open handoffs" value={m.handoffs_open} testId="studio-handoffs" />
        <StatTile label="Injection attempts · 24h" value={m.injection_attempts_24h} testId="studio-injection-24h" />
        <StatTile label="Orders delivered · 24h" value={m.orders_delivered_24h} testId="studio-delivered-24h" />
        <StatTile label="Auto-delivered · 24h" value={m.auto_delivered_24h} testId="studio-auto-delivered-24h" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="dt6-card p-6">
          <div className="dt6-chip mb-3">/ TOP HANDOFF REASONS</div>
          {(m.top_handoff_reasons || []).length === 0 ? (
            <EmptyState testId="studio-handoff-reasons-empty">NO HANDOFFS YET.</EmptyState>
          ) : (
            <ul className="space-y-2 text-sm">
              {m.top_handoff_reasons.map((r) => (
                <li key={r.reason} className="flex justify-between" data-testid={`studio-handoff-reason-${r.reason}`}>
                  <span className="font-mono text-xs">{r.reason}</span>
                  <span className="font-mono">{r.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="dt6-card p-6">
          <div className="dt6-chip mb-3">/ RUN NOW</div>
          <div className="space-y-3">
            <button className="dt6-btn w-full justify-center" onClick={runBrief} disabled={busy} data-testid="studio-run-brief">
              <Play size={14} /> Run daily brief
            </button>
            <button className="dt6-btn w-full justify-center" onClick={tickDelivery} disabled={busy} data-testid="studio-tick-delivery">
              <Play size={14} /> Sweep auto-delivery queue
            </button>
          </div>
          {brief && (
            <pre className="mt-4 p-3 rounded font-mono text-[0.7rem] whitespace-pre-wrap" style={{ background: "var(--dt6-bg)", border: "1px solid var(--dt6-border)" }} data-testid="studio-brief-output">
{JSON.stringify(brief, null, 2)}
            </pre>
          )}
          {tickResult && (
            <pre className="mt-4 p-3 rounded font-mono text-[0.7rem] whitespace-pre-wrap" style={{ background: "var(--dt6-bg)", border: "1px solid var(--dt6-border)" }} data-testid="studio-tick-output">
{JSON.stringify(tickResult, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </>
  );
}

function TogglesTab() {
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => { api.get("/admin/agent/toggles").then((r) => setValues(r.data)); }, []);

  if (!values) return <EmptyState testId="studio-toggles-loading">LOADING...</EmptyState>;

  const set = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));
  const save = async () => {
    setSaving(true); setMsg("");
    try {
      await api.put("/admin/agent/toggles", { values });
      setMsg("SAVED. Changes take effect immediately.");
    } catch (e) { setMsg(formatApiError(e)); }
    setSaving(false);
  };

  const flag = (k, label, sub) => (
    <div className="flex items-start justify-between py-3 border-b" style={{ borderColor: "var(--dt6-border)" }} data-testid={`toggle-${k}`}>
      <div>
        <div className="font-heading font-bold text-sm">{label}</div>
        {sub && <div className="text-xs text-[color:var(--dt6-muted)] mt-1">{sub}</div>}
      </div>
      <button className="dt6-btn text-xs" onClick={() => set(k, !values[k])} data-testid={`toggle-${k}-btn`}>
        {values[k] ? <><ToggleRight size={16} className="text-[color:var(--dt6-success)]" /> ON</>
                    : <><ToggleLeft size={16} className="text-[color:var(--dt6-muted)]" /> OFF</>}
      </button>
    </div>
  );

  const numField = (k, label) => (
    <div className="flex items-center justify-between py-3 border-b" style={{ borderColor: "var(--dt6-border)" }}>
      <div className="font-heading font-bold text-sm">{label}</div>
      <input
        className="dt6-input w-40 text-right font-mono text-xs"
        type="number"
        value={values[k] ?? 0}
        onChange={(e) => set(k, parseInt(e.target.value) || 0)}
        data-testid={`num-${k}`}
      />
    </div>
  );

  return (
    <div className="dt6-card p-6 max-w-3xl" data-testid="studio-toggles">
      <div className="dt6-chip mb-4">/ AGENT KILL SWITCHES & CONFIG</div>

      <div className="mb-6">
        <div className="font-mono text-[0.7rem] tracking-[0.14em] uppercase text-[color:var(--dt6-muted)] mb-2">
          / PER-FACE KILL SWITCHES (turning off routes callers to the honest "leave a message" fallback)
        </div>
        {flag("concierge_enabled", "Concierge (public) enabled")}
        {flag("copilot_enabled", "Copilot (client/partner portals) enabled")}
        {flag("ops_enabled", "Ops (admin) enabled")}
      </div>

      <div className="mb-6">
        <div className="font-mono text-[0.7rem] tracking-[0.14em] uppercase text-[color:var(--dt6-muted)] mb-2">
          / AUTO-DELIVERY (Phase 3 automation)
        </div>
        {flag("auto_delivery_kill_switch", "Kill switch — halt ALL auto-delivery",
          "Turning ON immediately pauses SIX's ability to auto-deliver orders. Orders fall back to human review.")}
        {numField("auto_approve_max_records", "Auto-approve record cap")}
      </div>

      <div className="mb-6">
        <div className="font-mono text-[0.7rem] tracking-[0.14em] uppercase text-[color:var(--dt6-muted)] mb-2">
          / OPS BEHAVIOR
        </div>
        {flag("auto_send_followups", "Auto-send drafted follow-up emails",
          "When OFF (default), SIX writes follow-ups to the outbox as DRAFT for one-click human send.")}
      </div>

      <div className="mb-6">
        <div className="font-mono text-[0.7rem] tracking-[0.14em] uppercase text-[color:var(--dt6-muted)] mb-2">
          / RATE LIMITS & BUDGETS
        </div>
        {numField("per_conversation_token_budget", "Per-conversation token budget (approx)")}
        {numField("per_ip_daily_message_cap", "Per-IP daily message cap")}
        {numField("daily_agent_spend_cap_usd", "Daily agent spend cap (USD)")}
      </div>

      <div className="flex justify-between items-center pt-2">
        {msg && <div className={`text-sm font-mono ${msg.startsWith('SAVED') ? 'text-[color:var(--dt6-success)]' : 'text-[color:var(--dt6-danger)]'}`} data-testid="toggles-msg">{msg}</div>}
        <button className="dt6-btn dt6-btn-primary text-xs" onClick={save} disabled={saving} data-testid="toggles-save">
          <Save size={12} /> {saving ? "SAVING..." : "Save config"}
        </button>
      </div>
    </div>
  );
}

function KnowledgeTab() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ topic: "", content: "" });
  const [msg, setMsg] = useState("");
  const load = () => api.get("/admin/agent/knowledge").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async (topic, content) => {
    setMsg("");
    try {
      await api.put(`/admin/agent/knowledge/${topic}`, { topic, content });
      await load();
      setSelected(null);
      setForm({ topic: "", content: "" });
      setMsg("SAVED.");
    } catch (e) { setMsg(formatApiError(e)); }
  };

  return (
    <>
      <div className="mb-4 text-xs font-mono tracking-[0.14em] uppercase text-[color:var(--dt6-muted)]">
        / SIX ANSWERS FROM THIS STORE ONLY. WHAT'S NOT HERE GETS ROUTED TO HUMAN HANDOFF.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="dt6-card p-4" data-testid="knowledge-list">
          <div className="dt6-chip mb-3">/ ENTRIES</div>
          {rows.length === 0 ? (
            <EmptyState testId="knowledge-empty">NO ENTRIES.</EmptyState>
          ) : (
            <ul className="space-y-2">
              {rows.map((r) => (
                <li key={r.topic}>
                  <button className={`w-full text-left dt6-card p-3 ${selected?.topic === r.topic ? "border-[color:var(--dt6-primary)]" : ""}`} onClick={() => setSelected(r)} data-testid={`knowledge-item-${r.topic}`}>
                    <div className="font-mono text-xs uppercase tracking-[0.14em] text-[color:var(--dt6-primary)]">{r.topic}</div>
                    <div className="text-xs text-[color:var(--dt6-muted)] mt-1 line-clamp-2">{r.content?.slice(0,140)}...</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="dt6-card p-4">
          <div className="dt6-chip mb-3">/ {selected ? `EDIT ${selected.topic}` : "NEW ENTRY"}</div>
          <input
            className="dt6-input mb-3 font-mono text-xs"
            placeholder="topic (lowercase, e.g. turnaround)"
            value={selected ? selected.topic : form.topic}
            onChange={(e) => selected ? setSelected({...selected, topic: e.target.value.toLowerCase()}) : setForm({...form, topic: e.target.value.toLowerCase()})}
            disabled={!!selected}
            data-testid="knowledge-topic"
          />
          <textarea
            className="dt6-textarea"
            rows={12}
            placeholder="content — this text is fed to SIX as source-of-truth for the topic"
            value={selected ? selected.content : form.content}
            onChange={(e) => selected ? setSelected({...selected, content: e.target.value}) : setForm({...form, content: e.target.value})}
            data-testid="knowledge-content"
          />
          {msg && <div className={`mt-3 text-xs font-mono ${msg.startsWith('SAVED') ? 'text-[color:var(--dt6-success)]' : 'text-[color:var(--dt6-danger)]'}`} data-testid="knowledge-msg">{msg}</div>}
          <div className="mt-4 flex justify-end gap-2">
            {selected && <button className="dt6-btn text-xs" onClick={() => setSelected(null)}>Cancel</button>}
            <button
              className="dt6-btn dt6-btn-primary text-xs"
              onClick={() => selected ? save(selected.topic, selected.content) : save(form.topic, form.content)}
              disabled={!(selected?.topic || form.topic)}
              data-testid="knowledge-save"
            >
              <Save size={12} /> {selected ? "Save changes" : "Create entry"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function ProposalsTab() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("proposed");
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");

  const load = () => api.get("/admin/agent/knowledge-proposals", { params: { status: filter } })
    .then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [filter]);

  const runScan = async () => {
    setScanning(true); setErr(""); setScanResult(null);
    try {
      const { data } = await api.post("/admin/agent/scan-knowledge-gaps", {
        lookback_days: 7, min_cluster_size: 1, max_proposals: 5,
      });
      setScanResult(data);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    setScanning(false);
  };

  const approve = async (id) => {
    setErr("");
    try { await api.post(`/admin/agent/knowledge-proposals/${id}/approve`); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const reject = async (id, reason) => {
    try { await api.post(`/admin/agent/knowledge-proposals/${id}/reject`, { reason }); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.put(`/admin/agent/knowledge-proposals/${editing.id}`, {
        proposed_topic: editing.proposed_topic,
        proposed_content: editing.proposed_content,
      });
      setEditing(null);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <>
      <div className="mb-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="font-mono text-[0.7rem] tracking-[0.14em] uppercase text-[color:var(--dt6-muted)]">/ FILTER</div>
          {["proposed","approved","rejected"].map((s) => (
            <button key={s} className={`dt6-btn text-xs ${filter===s?"dt6-btn-primary":""}`} onClick={() => setFilter(s)} data-testid={`proposals-filter-${s}`}>{s}</button>
          ))}
        </div>
        <button className="dt6-btn dt6-btn-primary text-xs" onClick={runScan} disabled={scanning} data-testid="proposals-scan">
          <Sparkles size={12} /> {scanning ? "SIX IS DRAFTING..." : "Run gap scan now"}
        </button>
      </div>

      {scanResult && (
        <div className="dt6-card p-4 mb-4" data-testid="proposals-scan-result">
          <div className="dt6-chip dt6-chip-primary mb-2">/ SCAN {scanResult.run_id?.slice(0,8)}</div>
          <div className="text-sm">
            Examined <b>{scanResult.clusters_examined}</b> handoff clusters. Created <b>{scanResult.proposals_created}</b> proposals · Skipped <b>{scanResult.proposals_skipped}</b>.
          </div>
          <div className="mt-2 text-xs text-[color:var(--dt6-muted)] font-mono">
            {(scanResult.details || []).map((d, i) => (
              <div key={i}>· {d.reason} → {d.outcome}{d.topic ? ` (topic: ${d.topic})` : ""}</div>
            ))}
          </div>
        </div>
      )}

      {err && <div className="mb-3 text-sm text-[color:var(--dt6-danger)]" data-testid="proposals-err">{err}</div>}

      {rows.length === 0 ? (
        <EmptyState testId="proposals-empty">
          {filter === "proposed"
            ? "NO PENDING PROPOSALS. RUN A GAP SCAN TO HAVE SIX DRAFT NEW ENTRIES FROM RECENT HANDOFFS."
            : `NO ${filter.toUpperCase()} PROPOSALS.`}
        </EmptyState>
      ) : (
        <div className="space-y-4">
          {rows.map((p) => (
            <div key={p.id} className="dt6-card p-5" data-testid={`proposal-${p.id}`}>
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`dt6-chip ${p.proposed_action === "update" ? "dt6-chip-amber" : "dt6-chip-primary"}`}>
                      {p.proposed_action === "update" ? "UPDATE" : "CREATE"}
                    </span>
                    <span className="font-mono text-xs uppercase tracking-[0.14em] text-[color:var(--dt6-primary)]">
                      {p.proposed_topic}
                    </span>
                    <span className="dt6-chip">{p.gap_handoff_count} handoff{p.gap_handoff_count !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="text-xs text-[color:var(--dt6-muted)]">
                    Gap reason: <span className="font-mono">{p.gap_reason}</span> · drafted {new Date(p.created_at).toLocaleString()}
                  </div>
                </div>
                {p.status === "proposed" && (
                  <div className="flex gap-2 shrink-0">
                    <button className="dt6-btn text-xs" onClick={() => setEditing({ ...p })} data-testid={`proposal-edit-${p.id}`}>
                      <Edit3 size={12} /> Edit
                    </button>
                    <button className="dt6-btn text-xs" onClick={() => reject(p.id, "not_useful")} data-testid={`proposal-reject-${p.id}`}>
                      <X size={12} /> Reject
                    </button>
                    <button className="dt6-btn dt6-btn-primary text-xs" onClick={() => approve(p.id)} data-testid={`proposal-approve-${p.id}`}>
                      <Check size={12} /> Approve
                    </button>
                  </div>
                )}
                {p.status !== "proposed" && (
                  <span className={`dt6-chip ${p.status === "approved" ? "dt6-chip-success" : "dt6-chip-danger"}`}>
                    {p.status.toUpperCase()}
                  </span>
                )}
              </div>
              <div className="text-sm p-3 rounded" style={{ background: "var(--dt6-bg)", border: "1px solid var(--dt6-border)" }}>
                {p.proposed_content}
              </div>
              {p.curator_reasoning && (
                <div className="mt-2 text-xs text-[color:var(--dt6-muted)] italic">
                  Curator note: {p.curator_reasoning}
                </div>
              )}
              {p.gap_sample_summaries && p.gap_sample_summaries.length > 0 && (
                <details className="mt-3">
                  <summary className="text-xs font-mono tracking-[0.14em] uppercase text-[color:var(--dt6-muted)] cursor-pointer">
                    / Handoff summaries that triggered this ({p.gap_sample_summaries.length})
                  </summary>
                  <ul className="mt-2 space-y-1 text-xs text-[color:var(--dt6-muted)]">
                    {p.gap_sample_summaries.slice(0, 5).map((s, i) => (
                      <li key={i} className="border-l-2 pl-3" style={{ borderColor: "var(--dt6-border-2)" }}>{s}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="dt6-card p-6 w-full max-w-2xl" style={{ background: "var(--dt6-surface)" }} onClick={(e) => e.stopPropagation()} data-testid="proposal-editor">
            <div className="dt6-chip mb-3">/ EDIT PROPOSAL</div>
            <label className="dt6-label">Topic</label>
            <input className="dt6-input mb-3 font-mono text-xs" value={editing.proposed_topic} onChange={(e) => setEditing({ ...editing, proposed_topic: e.target.value.toLowerCase() })} data-testid="proposal-editor-topic" />
            <label className="dt6-label">Content</label>
            <textarea className="dt6-textarea" rows={10} value={editing.proposed_content} onChange={(e) => setEditing({ ...editing, proposed_content: e.target.value })} data-testid="proposal-editor-content" />
            <div className="mt-4 flex justify-end gap-2">
              <button className="dt6-btn text-xs" onClick={() => setEditing(null)}>Cancel</button>
              <button className="dt6-btn dt6-btn-primary text-xs" onClick={saveEdit} data-testid="proposal-editor-save">Save changes</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


function HandoffsTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/agent/handoffs").then((r) => setRows(r.data)); }, []);
  if (rows.length === 0) return <EmptyState testId="handoffs-empty">NO OPEN HANDOFFS.</EmptyState>;
  return (
    <div className="space-y-3">
      {rows.map((h) => (
        <div key={h.id} className="dt6-card p-5" data-testid={`handoff-${h.id}`}>
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="dt6-chip mb-1">{h.face?.toUpperCase()}</div>
              <div className="font-heading font-bold">{h.reason}</div>
              <div className="text-xs text-[color:var(--dt6-muted)]">{h.user_email || "anonymous"} · {new Date(h.created_at).toLocaleString()}</div>
            </div>
            <span className={`dt6-chip ${h.status === "new" ? "dt6-chip-amber" : ""}`}>{h.status?.toUpperCase()}</span>
          </div>
          <div className="text-sm text-[color:var(--dt6-muted)] mt-2">{h.summary}</div>
          <div className="text-[0.65rem] font-mono text-[color:var(--dt6-muted)] mt-2">
            conv: {String(h.conversation_id).slice(0, 8).toUpperCase()}
          </div>
        </div>
      ))}
    </div>
  );
}

function InjectionsTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/agent/injection-flags").then((r) => setRows(r.data)); }, []);
  if (rows.length === 0) {
    return (
      <EmptyState testId="injections-empty">
        <Shield size={24} className="mx-auto mb-2 text-[color:var(--dt6-success)]" />
        NO INJECTION ATTEMPTS LOGGED.
      </EmptyState>
    );
  }
  return (
    <div className="dt6-card overflow-hidden">
      <div className="mb-2 px-4 pt-4 flex items-center gap-2 text-xs text-[color:var(--dt6-danger)]">
        <ShieldOff size={14} /> Injection attempts are blocked in code; this log is for review.
      </div>
      <table className="dt6-table">
        <thead><tr><th>When</th><th>Face</th><th>User</th><th>IP</th><th>Reason</th><th>Conv</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} data-testid={`injection-row-${r.id}`}>
              <td className="font-mono text-xs">{new Date(r.created_at).toLocaleString()}</td>
              <td className="font-mono">{r.face}</td>
              <td className="text-xs">{r.user_id ? String(r.user_id).slice(0, 8) : "anon"}</td>
              <td className="text-xs font-mono">{r.ip}</td>
              <td className="text-xs">{r.reason}</td>
              <td className="font-mono text-xs">{String(r.conversation_id).slice(0, 8)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FunnelTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [digestBusy, setDigestBusy] = useState(false);
  const [digestMsg, setDigestMsg] = useState("");
  const [impr, setImpr] = useState(null);
  const load = () => {
    setLoading(true);
    api.get("/admin/demo-funnel")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    api.get("/admin/badge-impressions?days=7").then((r) => setImpr(r.data)).catch(() => setImpr(null));
  };
  useEffect(load, []);

  const sendDigest = async () => {
    setDigestBusy(true); setDigestMsg("");
    try {
      const { data: r } = await api.post("/admin/digest/weekly");
      const sent = (r.recipients || []).filter((x) => x.sent_status === "sent").length;
      const logged = (r.recipients || []).filter((x) => x.sent_status === "logged").length;
      setDigestMsg(`Digest fired to ${r.recipients?.length || 0} admin(s) — sent=${sent}, outbox-logged=${logged}.`);
    } catch (e) {
      setDigestMsg("Digest failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setDigestBusy(false);
    }
  };

  if (loading && !data) return <div className="text-sm text-[color:var(--dt6-muted)]" data-testid="funnel-loading">Loading funnel…</div>;
  if (!data) return <EmptyState title="No demo activity yet" body="Once prospects hit the 'Try SIX Live' CTA and complete deliveries, the conversion funnel will populate here." />;

  const t = data.totals || {};
  const funnel = data.funnel || [];
  const top = funnel[0]?.distinct_users || 1;

  return (
    <div data-testid="funnel-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-heading text-2xl">Demo Conversion Funnel</h2>
        <div className="flex gap-2">
          <button
            className="dt6-btn text-xs"
            onClick={sendDigest}
            disabled={digestBusy}
            data-testid="funnel-send-digest"
            title="Send the weekly digest to all admin users now"
          >
            {digestBusy ? "SENDING…" : "Send weekly digest now"}
          </button>
          <button className="dt6-btn text-xs" onClick={load} data-testid="funnel-refresh">Refresh</button>
        </div>
      </div>
      {digestMsg && (
        <div className="text-xs text-[color:var(--dt6-muted)] mb-3 font-mono" data-testid="funnel-digest-msg">
          {digestMsg}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatTile label="Total signups" value={t.signups || 0} testId="funnel-stat-signups" />
        <StatTile label="First deliveries" value={t.first_delivery || 0} testId="funnel-stat-firstdel" />
        <StatTile label="Nudges shown" value={t.nudge_shown || 0} testId="funnel-stat-nudged" />
        <StatTile label="Booked demos" value={t.nudge_clicked_book || 0} testId="funnel-stat-booked" accent />
      </div>

      <div className="dt6-card p-6 mb-6" data-testid="funnel-bars">
        <div className="text-xs uppercase tracking-wide text-[color:var(--dt6-muted)] mb-4 font-mono">
          / stage-by-stage distinct users
        </div>
        {funnel.map((row) => {
          const pct = top > 0 ? Math.round((row.distinct_users / top) * 100) : 0;
          return (
            <div key={row.stage} className="mb-3" data-testid={`funnel-row-${row.stage}`}>
              <div className="flex justify-between text-xs mb-1">
                <span className="font-mono uppercase text-[color:var(--dt6-muted)]">{row.stage.replace(/_/g," ")}</span>
                <span className="font-mono">{row.distinct_users} · {pct}%</span>
              </div>
              <div className="h-2 rounded" style={{ background: "rgba(255,255,255,0.06)" }}>
                <div
                  className="h-full rounded"
                  style={{ width: `${pct}%`, background: "var(--dt6-primary)", transition: "width 0.4s" }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="dt6-card p-6">
        <div className="text-xs uppercase tracking-wide text-[color:var(--dt6-muted)] mb-4 font-mono">
          / recent conversion events
        </div>
        {(data.recent || []).length === 0 ? (
          <div className="text-sm text-[color:var(--dt6-muted)]">No events yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="funnel-recent">
            <thead>
              <tr className="text-left text-xs uppercase text-[color:var(--dt6-muted)]">
                <th className="pb-2">When</th>
                <th className="pb-2">Email</th>
                <th className="pb-2">Event</th>
              </tr>
            </thead>
            <tbody>
              {(data.recent || []).slice(0, 20).map((r) => (
                <tr key={r.id} className="border-t border-[color:var(--dt6-border)]">
                  <td className="py-2 font-mono text-xs">{String(r.at || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="py-2">{r.demo_email || "—"}</td>
                  <td className="py-2 font-mono text-xs">{r.event}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Badge impressions — top embed sites & top orgs */}
      {impr && (impr.totals.impressions > 0 || (impr.top_sites || []).length > 0) && (
        <div className="dt6-card p-6 mt-6" data-testid="badge-impressions">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="text-xs uppercase tracking-wide text-[color:var(--dt6-muted)] font-mono">
              / STANDARD-VERIFIED BADGE · TOP EMBEDS (LAST {impr.window_days}D)
            </div>
            <div className="font-mono text-xs" data-testid="badge-impressions-totals">
              {impr.totals.impressions} impressions · {impr.totals.distinct_hosts} sites
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="text-xs uppercase text-[color:var(--dt6-muted)] mb-2 font-mono">/ TOP SITES</div>
              {(impr.top_sites || []).length === 0 ? (
                <div className="text-xs text-[color:var(--dt6-muted)]">No embeds detected yet.</div>
              ) : (
                <table className="w-full text-sm" data-testid="badge-top-sites">
                  <thead>
                    <tr className="text-left text-[0.65rem] uppercase text-[color:var(--dt6-muted)] font-mono">
                      <th className="pb-2">Host</th>
                      <th className="pb-2 text-right">Hits</th>
                      <th className="pb-2 text-right">Orgs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(impr.top_sites || []).slice(0, 10).map((s) => (
                      <tr key={s.host} className="border-t border-[color:var(--dt6-border)]">
                        <td className="py-2 font-mono text-xs">{s.host}</td>
                        <td className="py-2 font-mono text-xs text-right">{s.hits}</td>
                        <td className="py-2 font-mono text-xs text-right">{s.distinct_orgs}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div>
              <div className="text-xs uppercase text-[color:var(--dt6-muted)] mb-2 font-mono">/ TOP EVANGELIZING ORGS</div>
              {(impr.top_orgs || []).length === 0 ? (
                <div className="text-xs text-[color:var(--dt6-muted)]">No approved-org embeds detected yet.</div>
              ) : (
                <table className="w-full text-sm" data-testid="badge-top-orgs">
                  <thead>
                    <tr className="text-left text-[0.65rem] uppercase text-[color:var(--dt6-muted)] font-mono">
                      <th className="pb-2">Org</th>
                      <th className="pb-2 text-right">Hits</th>
                      <th className="pb-2 text-right">Sites</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(impr.top_orgs || []).slice(0, 10).map((o) => (
                      <tr key={o.badge_slug} className="border-t border-[color:var(--dt6-border)]">
                        <td className="py-2 text-xs">{o.org_name}</td>
                        <td className="py-2 font-mono text-xs text-right">{o.hits}</td>
                        <td className="py-2 font-mono text-xs text-right">{o.distinct_hosts}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

